import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import { useSettings } from "@/hooks/useSettings";

const AdminShipping = () => {
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const [zones, setZones] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ id: "", zone_name: "", charge: 0 });

  const load = async () => {
    const { data } = await supabase.from("shipping_zones").select("*").order("charge");
    setZones(data ?? []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.zone_name) return toast.error("Zone name is required");
    const payload = { zone_name: form.zone_name, charge: Number(form.charge) };
    const { error } = form.id
      ? await supabase.from("shipping_zones").update(payload).eq("id", form.id)
      : await supabase.from("shipping_zones").insert(payload);
    if (error) return toast.error(error.message);
    toast.success("Saved");
    setOpen(false);
    setForm({ id: "", zone_name: "", charge: 0 });
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("shipping_zones").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Shipping zones</h1>
          <p className="text-muted-foreground">Manage delivery rates</p>
        </div>
        <Button variant="hero" onClick={() => { setForm({ id: "", zone_name: "", charge: 0 }); setOpen(true); }}>
          <Plus className="h-4 w-4" /> Add zone
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {zones.map((z) => (
          <Card key={z.id} className="p-5 border-0 shadow-soft flex items-center justify-between">
            <div>
              <p className="font-medium">{z.zone_name}</p>
              <p className="text-sm text-muted-foreground">{formatCurrency(Number(z.charge), symbol)} per order</p>
            </div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => { setForm({ id: z.id, zone_name: z.zone_name, charge: z.charge }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(z.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? "Edit zone" : "New zone"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Zone name</Label><Input value={form.zone_name} onChange={(e) => setForm({ ...form, zone_name: e.target.value })} /></div>
            <div><Label>Shipping charge ({symbol})</Label><Input type="number" value={form.charge} onChange={(e) => setForm({ ...form, charge: Number(e.target.value) })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button variant="hero" onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminShipping;
